import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

// Import a header image
import headerImage from '../assets/images/Wedding Services/2.jpg';
import weddingIntroImage from '../assets/images/Wedding Services/3.jpg';

// Import service images
import poruwaCeremonyImage from '../assets/images/Wedding Services/4.jpg';
import ceremonialDrummingImage from '../assets/images/Wedding Services/5.jpg';
import traditionalDanceImage from '../assets/images/Wedding Services/2.jpg';
import completeEntertainmentImage from '../assets/images/Wedding Services/3.jpg';

const WeddingServicesPage: React.FC = () => {
  // Wedding services
  const services = [
    {
      id: 1,
      title: "Traditional Poruwa Ceremonies",
      description: "Authentic Sri Lankan Poruwa ceremonies with traditional elements and customs",
      image: poruwaCeremonyImage,
      features: [
        "Professional Poruwa setup with traditional decorations",
        "Ceremonial drums and traditional music",
        "Ashtaka chanting by professional presenters",
        "Customizable elements to match your wedding theme"
      ]
    },
    {
      id: 2,
      title: "Ceremonial Drumming",
      description: "Energetic and rhythmic drumming performances to elevate your wedding atmosphere",
      image: ceremonialDrummingImage,
      features: [
        "Traditional Magul Bera (wedding drums) performance",
        "Entrance procession with ceremonial drumming",
        "Interactive performances with guests",
        "Various traditional drumming styles"
      ]
    },
    {
      id: 3,
      title: "Traditional Dance Performances",
      description: "Elegant and vibrant traditional Sri Lankan dance performances for your special day",
      image: traditionalDanceImage,
      features: [
        "Kandyan dance performances",
        "Low country dance styles",
        "Customized choreography for your event",
        "Stunning traditional costumes"
      ]
    },
    {
      id: 4,
      title: "Complete Wedding Entertainment",
      description: "Full-service entertainment packages for an unforgettable wedding experience",
      image: completeEntertainmentImage,
      features: [
        "Combined drumming and dance performances",
        "Reception entertainment options",
        "Cultural demonstrations for guests"
        
      ]
    }
  ];

  // Pricing packages
  const packages = [
    {
      id: 1,
      name: "Essential",
      price: "60,000 LKR",
      description: "Perfect for intimate ceremonies",
      features: [
        "4 traditional dancers",
        "2 ceremonial drummers",
        "1 conch player",
        "1 Ashtaka presenter",
        "4 Jayamangala girls",
        "Complete Poruwa goods",
      ],
      limitation: "Available within Colombo area only"
    },
    {
      id: 2,
      name: "Premium",
      price: "75,000 LKR",
      description: "Our most popular package",
      features: [
        "6 traditional dancers",
        "4 ceremonial drummers",
        "1 conch player",
        "1 Ashtaka presenter",
        "4 Jayamangala girls",
        "Complete Poruwa goods",
      ],
      limitation: "Available within Colombo area only",
      popular: true
    },
    {
      id: 3,
      name: "Luxury",
      price: "150,000 LKR",
      description: "The ultimate wedding experience",
      features: [
        "12 traditional dancers",
        "8 ceremonial drummers",
        "2 conch players",
        "1 Ashtaka presenter",
        "6 Jayamangala girls",
        "Premium Poruwa goods",
      ],
      limitation: "Available within Colombo area only"
    }
  ];

  return (
    <>
      <PageHeader
        title="Traditional Wedding Services"
        subtitle="Authentic Sri Lankan wedding ceremonies and cultural entertainment"
        backgroundImage={headerImage}
      >
        <Link
          to="/contact#booking"
          className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-orange-600 hover:bg-orange-700 transition-all shadow-lg"
        >
          Book Your Wedding
        </Link>
      </PageHeader>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              Make Your Special Day <span className="text-orange-600">Extraordinary</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Rivisara brings the richness of Sri Lankan culture to your wedding celebration with 
              authentic traditional performances, ceremonies, and entertainment.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Wedding Philosophy</h3>
              <p className="text-gray-600 mb-4">
                At Rivisara, we believe that weddings should be a perfect blend of tradition, 
                elegance, and personal expression. Our mission is to create unforgettable wedding 
                experiences that honor Sri Lankan cultural heritage while celebrating your unique love story.
              </p>
              <p className="text-gray-600">
                With extensive experience in traditional Poruwa ceremonies and wedding entertainment, 
                our professional team ensures every detail is meticulously planned and executed to perfection, 
                allowing you to relax and enjoy your special day.
              </p>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <div className="font-bold text-orange-600">100+</div>
                  <div className="text-sm text-gray-600">Weddings Per Year</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <div className="font-bold text-orange-600">15+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-orange-500 rounded-tr-3xl rounded-bl-3xl transform rotate-3"></div>
                <img
                  src={weddingIntroImage}
                  alt="Traditional Sri Lankan wedding ceremony"
                  className="relative z-10 rounded-tr-3xl rounded-bl-3xl shadow-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              Our Wedding <span className="text-orange-600">Services</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              From traditional Poruwa ceremonies to exhilarating dance performances, 
              we provide comprehensive cultural elements for your wedding
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 gap-12">
            {services.map((service, index) => (
              <motion.div 
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className={`grid md:grid-cols-2 ${
                  index % 2 !== 0 ? 'md:grid-flow-row-dense' : ''
                }`}>
                  <div className="h-64 md:h-auto">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                    <p className="mt-2 text-gray-600">{service.description}</p>
                    
                    <ul className="mt-6 space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <svg className="flex-shrink-0 h-6 w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="ml-3 text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="mt-8">
                      <Link
                        to="/contact#booking"
                        className="inline-flex items-center px-4 py-2 border border-orange-600 rounded-md text-orange-600 hover:bg-orange-50"
                      >
                        Inquire About This Service
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              Wedding <span className="text-orange-600">Packages</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the perfect package for your special day or contact us for a custom solution
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: pkg.id * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all hover:-translate-y-1 hover:shadow-2xl ${
                  pkg.popular ? 'ring-2 ring-orange-500 ring-offset-2' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="bg-orange-500 text-white py-2 text-center">
                    <p className="text-sm font-medium">Most Popular</p>
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900">{pkg.name}</h3>
                  <p className="mt-2 text-3xl font-bold text-orange-600">{pkg.price}</p>
                  <p className="mt-1 text-gray-500">{pkg.description}</p>
                  
                  <ul className="mt-8 space-y-4">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="flex-shrink-0 h-6 w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="ml-3 text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {pkg.limitation && (
                    <div className="mt-4 text-sm text-gray-500 italic">
                      <span className="flex items-start">
                        <svg className="flex-shrink-0 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="ml-2">{pkg.limitation}</span>
                      </span>
                    </div>
                  )}
                  
                  <div className="mt-8">
                    <Link
                      to="/contact#booking"
                      className={`block w-full py-3 px-4 rounded-md text-center font-medium ${
                        pkg.popular 
                          ? 'bg-orange-600 text-white hover:bg-orange-700' 
                          : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      }`}
                    >
                      Select This Package
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-16 bg-orange-50 rounded-xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900">Need a Custom Package?</h3>
            <p className="mt-2 text-gray-600 max-w-2xl mx-auto">
              We understand that every wedding is unique. Contact us to create a custom package tailored to your specific requirements and vision.
            </p>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
              >
                Get in Touch for Custom Options
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-20 bg-gray-900 text-white relative">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900 to-orange-800 opacity-20"></div>
        <div className="absolute inset-0 bg-pattern-dark opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display sm:text-4xl">
              What Our <span className="text-orange-400">Couples Say</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <p className="text-gray-300 italic leading-relaxed">
                "The Poruwa ceremony conducted by Rivisara was absolutely magical. The drumming and dance performances added a truly authentic touch to our wedding. Our guests are still talking about it months later!"
              </p>
              <div className="mt-6 flex items-center">
                <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center font-bold">
                  LM
                </div>
                <div className="ml-3">
                  <p className="font-medium">Lakmini & Mahesh</p>
                  <p className="text-sm text-gray-400">Married June 2023</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <p className="text-gray-300 italic leading-relaxed">
                "We had a fusion wedding combining Sri Lankan and Western elements. Rivisara perfectly executed the traditional aspects while being flexible with our unique needs. Truly professional service!"
              </p>
              <div className="mt-6 flex items-center">
                <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center font-bold">
                  SD
                </div>
                <div className="ml-3">
                  <p className="font-medium">Samantha & Daniel</p>
                  <p className="text-sm text-gray-400">Married March 2023</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <p className="text-gray-300 italic leading-relaxed">
                "The drummers and dancers from Rivisara made our entrance so special. The energy they brought was incredible. Even our international guests were amazed by the performance and cultural experience."
              </p>
              <div className="mt-6 flex items-center">
                <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center font-bold">
                  NP
                </div>
                <div className="ml-3">
                  <p className="font-medium">Nimal & Priyanthi</p>
                  <p className="text-sm text-gray-400">Married November 2022</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:max-w-xl">
              <h2 className="text-3xl font-bold font-display tracking-tight">
                Ready to make your wedding day extraordinary?
              </h2>
              <p className="mt-3 text-lg text-orange-100">
                Contact us today to discuss how we can bring traditional Sri Lankan elements to your special day.
              </p>
            </div>
            <div className="mt-8 lg:mt-0 flex flex-col sm:flex-row sm:space-x-4">
              <Link
                to="/contact#booking"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-orange-600 bg-white hover:bg-orange-50 shadow-lg mb-4 sm:mb-0"
              >
                Book Your Wedding
              </Link>
              <Link
                to="/gallery"
                className="inline-flex items-center justify-center px-5 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-orange-700 shadow-lg"
              >
                View Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default WeddingServicesPage;
